import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Filter, Eye, Edit2, Trash2, FileText, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format date to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

export default function PurchaseOrdersList() {
  const [pos, setPOs] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPOs();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    filterPOs();
  }, [searchTerm, statusFilter, pos]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
  };

  const canCreatePO = currentUser?.role === 'admin' || ['stitching', 'accounts', 'maintenance', 'marketing', 'accessories', 'stitching/accessories'].includes(currentUser?.department?.toLowerCase());
  const isDyeing = currentUser?.department?.toLowerCase() === 'dyeing';

  const fetchPOs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPOs(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch POs:", err);
      setLoading(false);
    }
  };

  const filterPOs = async () => {
    if (searchTerm && searchTerm.length >= 2) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/purchase-orders/search/${encodeURIComponent(searchTerm)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let filtered = response.data;
        
        if (statusFilter !== "all") {
          filtered = filtered.filter(po => po.status === statusFilter);
        }
        
        setFilteredPOs(filtered);
      } catch (err) {
        localFilter();
      }
    } else {
      localFilter();
    }
  };

  const localFilter = () => {
    let filtered = pos;

    if (statusFilter !== "all") {
      filtered = filtered.filter(po => po.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(po =>
        po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.customer_po_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPOs(filtered);
  };

  const deletePO = async (id, poNumber) => {
    if (!window.confirm(`Delete ${poNumber}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/purchase-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPOs();
    } catch (err) {
      console.error("Failed to delete PO:", err);
      alert("Failed to delete purchase order");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Sent</Badge>;
      case 'received':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Received</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Cancelled</Badge>;
      default:
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    if (isDyeing) return '***';
    return `₹${amount?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="pos-title">
            <FileText className="w-6 h-6" />
            Purchase Orders
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Manage all vendor purchase orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPOs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {canCreatePO && (
            <Link to="/purchase-orders/new">
              <Button size="sm" data-testid="create-new-po-button">
                <Plus className="w-4 h-4 mr-2" />
                New PO
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by PO number, vendor, customer PO..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('all')}>
          <CardHeader className="pb-2">
            <CardDescription>Total Orders</CardDescription>
            <CardTitle className="text-2xl">{pos.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-blue-500" onClick={() => setStatusFilter('sent')}>
          <CardHeader className="pb-2">
            <CardDescription className="text-blue-600">Sent</CardDescription>
            <CardTitle className="text-2xl text-blue-600">{pos.filter(p => p.status === 'sent').length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-green-500" onClick={() => setStatusFilter('received')}>
          <CardHeader className="pb-2">
            <CardDescription className="text-green-600">Received</CardDescription>
            <CardTitle className="text-2xl text-green-600">{pos.filter(p => p.status === 'received').length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="cursor-pointer hover:shadow-md border-l-4 border-l-yellow-500" onClick={() => setStatusFilter('draft')}>
          <CardHeader className="pb-2">
            <CardDescription className="text-yellow-600">Draft</CardDescription>
            <CardTitle className="text-2xl text-yellow-600">{pos.filter(p => p.status === 'draft').length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* PO Table - Desktop */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          {filteredPOs.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-pos-found">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">No purchase orders found</p>
            </div>
          ) : (
            <Table data-testid="pos-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-bold">PO Number</TableHead>
                  <TableHead className="font-bold">Vendor</TableHead>
                  <TableHead className="font-bold">Customer PO</TableHead>
                  <TableHead className="font-bold text-right">{isDyeing ? 'Total' : 'Total (₹)'}</TableHead>
                  <TableHead className="font-bold">Delivery Date</TableHead>
                  <TableHead className="font-bold text-center">Status</TableHead>
                  <TableHead className="font-bold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPOs.map((po) => (
                  <TableRow key={po.id} className="hover:bg-muted/30">
                    <TableCell className="font-medium">{po.po_number}</TableCell>
                    <TableCell>{po.vendor_name}</TableCell>
                    <TableCell>
                      {po.customer_po_number ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">{po.customer_po_number}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(po.total)}</TableCell>
                    <TableCell>{formatDate(po.delivery_date)}</TableCell>
                    <TableCell className="text-center">{getStatusBadge(po.status)}</TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        <Button variant="outline" size="sm" className="h-10 w-10 min-w-[44px] min-h-[44px]" onClick={() => navigate(`/purchase-orders/${po.id}`)} title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canCreatePO && (
                          <>
                            <Button variant="outline" size="sm" className="h-10 w-10 min-w-[44px] min-h-[44px]" onClick={() => navigate(`/purchase-orders/${po.id}/edit`)} title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deletePO(po.id, po.po_number)} title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PO Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredPOs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">No purchase orders found</p>
            </CardContent>
          </Card>
        ) : (
          filteredPOs.map((po) => (
            <Card key={po.id} className="hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-lg">{po.po_number}</p>
                    <p className="text-sm text-muted-foreground">{po.vendor_name}</p>
                  </div>
                  {getStatusBadge(po.status)}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <p className="text-muted-foreground">Total</p>
                    <p className="font-semibold">{formatCurrency(po.total)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Delivery</p>
                    <p className="font-semibold">{formatDate(po.delivery_date)}</p>
                  </div>
                </div>
                {po.customer_po_number && (
                  <p className="text-xs text-muted-foreground mb-3">Customer PO: {po.customer_po_number}</p>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" className="flex-1 h-10" onClick={() => navigate(`/purchase-orders/${po.id}`)}>
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {canCreatePO && (
                    <>
                      <Button variant="outline" size="sm" className="flex-1 h-10" onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}>
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="h-10 text-red-600" onClick={() => deletePO(po.id, po.po_number)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
